import { Box, Divider, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import LinkIcon from "@mui/icons-material/Link";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import CheckIcon from "@mui/icons-material/Check";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import ImageIcon from "@mui/icons-material/Image";
import HideImageIcon from "@mui/icons-material/HideImage";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import CloseIcon from "@mui/icons-material/Close";
import GridOnIcon from "@mui/icons-material/GridOn";
import { Editor, useEditorState } from "@tiptap/react";
import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { DotLegalButton, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { commonEmojis } from "./MentionInputToolbar.types.ts";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { isNullOrWhitespace } from "@common/stringOperations.ts";

export interface MentionInputToolbarProps {
    editor: Editor;
    onMentionClick?: () => void;
    display: boolean;
    onFocusChange: (focus: boolean) => void;
    disableMentions: boolean;
    enableImageUpload?: boolean;
}

export interface MentionInputToolbarRef {
    openImageMenu: (anchorEl: HTMLElement) => void;
}

const TABLE_PICKER_ROWS = 8;
const TABLE_PICKER_COLUMNS = 8;
const TABLE_PICKER_CELL_SIZE = 18;

const MentionInputToolbar = forwardRef<MentionInputToolbarRef, MentionInputToolbarProps>(
    ({ editor, onMentionClick, display, onFocusChange, disableMentions, enableImageUpload }, ref) => {
        // v3 no longer re-renders on every transaction, so anything rendered from editor state has
        // to subscribe to it explicitly. Only render-time reads belong here - the click handlers
        // below keep reading the editor directly, which is already correct and always current.
        const toolbarState = useEditorState({
            editor,
            selector: ({ editor }) => ({
                isBold: editor.isActive("bold"),
                isItalic: editor.isActive("italic"),
                isStrike: editor.isActive("strike"),
                isLink: editor.isActive("link"),
                isBulletList: editor.isActive("bulletList"),
                isOrderedList: editor.isActive("orderedList"),
                isImage: editor.isActive("image"),
                isTable: editor.isActive("table"),
                isHeading: editor.isActive("heading"),
                headingLevel: [1, 2, 3, 4].find((level) => editor.isActive("heading", { level })),
            }),
        });

        const [emojiAnchorEl, setEmojiAnchorEl] = useState<null | HTMLElement>(null);
        const emojiOpen = Boolean(emojiAnchorEl);

        const [headingAnchorEl, setHeadingAnchorEl] = useState<null | HTMLElement>(null);
        const headingOpen = Boolean(headingAnchorEl);

        const [linkAnchorEl, setLinkAnchorEl] = useState<null | HTMLElement>(null);
        const linkOpen = Boolean(linkAnchorEl);
        const [linkUrl, setLinkUrl] = useState<string>("");

        const [imageAnchorEl, setImageAnchorEl] = useState<null | HTMLElement>(null);
        const imageOpen = Boolean(imageAnchorEl);
        const [imageUrl, setImageUrl] = useState<string>("");
        const [imageWidth, setImageWidth] = useState<string>("");
        const [imageHeight, setImageHeight] = useState<string>("");
        const [imageAlign, setImageAlign] = useState<string>("left");
        const [isEditingImage, setIsEditingImage] = useState<boolean>(false);
        const imageMenuRef = useRef<HTMLDivElement>(null);

        const [tableAnchorEl, setTableAnchorEl] = useState<null | HTMLElement>(null);
        const tableOpen = Boolean(tableAnchorEl);
        const [tablePickerSize, setTablePickerSize] = useState<{ rows: number; columns: number } | null>(null);

        const { translateString } = useTranslation();

        const onImageUrlChange = (url: string) => {
            setImageUrl(url);

            if (!url) return;

            const userHasSetWidth = imageWidth !== "" && imageWidth !== "0";

            if (userHasSetWidth) return;

            const img = new Image();
            img.onload = () => {
                setImageWidth(String(img.naturalWidth));
            };
            img.src = url;
        };

        const handleImageKeyDown = (e: React.KeyboardEvent) => {
            if (e.key === "Tab") {
                const container = imageMenuRef.current;
                if (!container) return;

                const focusableElements = container.querySelectorAll<HTMLElement>("input, button, [tabindex]:not([tabindex='-1'])");
                const focusableArray = Array.from(focusableElements);
                const currentIndex = focusableArray.findIndex((el) => el === document.activeElement || el.contains(document.activeElement as Node));

                if (focusableArray.length === 0) return;

                e.preventDefault();
                e.stopPropagation();

                let nextIndex: number;
                if (e.shiftKey) {
                    nextIndex = currentIndex <= 0 ? focusableArray.length - 1 : currentIndex - 1;
                } else {
                    nextIndex = currentIndex >= focusableArray.length - 1 ? 0 : currentIndex + 1;
                }

                focusableArray[nextIndex]?.focus();
            }
        };

        const openImageMenu = (anchorEl: HTMLElement) => {
            const imageAttrs = editor.getAttributes("image");
            if (editor.isActive("image") && imageAttrs.src) {
                setImageUrl(imageAttrs.src || "");
                setImageWidth(imageAttrs.width ? String(imageAttrs.width) : "");
                setImageHeight(imageAttrs.height ? String(imageAttrs.height) : "");
                setImageAlign(imageAttrs.align || "left");
                setIsEditingImage(true);
            } else {
                setImageUrl("");
                setImageWidth("");
                setImageHeight("");
                setImageAlign("left");
                setIsEditingImage(false);
            }
            setImageAnchorEl(anchorEl);
            onFocusChange(true);
        };

        useImperativeHandle(ref, () => ({
            openImageMenu,
        }));

        const handleHeadingClick = (event: React.MouseEvent<HTMLElement>) => {
            setHeadingAnchorEl(event.currentTarget);
            onFocusChange(true);
        };

        const handleHeadingClose = () => {
            setHeadingAnchorEl(null);
            onFocusChange(false);
        };

        const handleHeadingSelect = (level: 0 | 1 | 2 | 3 | 4) => {
            if (level === 0) {
                editor.chain().focus().setParagraph().run();
            } else {
                editor
                    .chain()
                    .focus()
                    .toggleHeading({ level: level as 1 | 2 | 3 | 4 })
                    .run();
            }
            handleHeadingClose();
        };

        const handleTableClick = (event: React.MouseEvent<HTMLElement>) => {
            setTablePickerSize(null);
            setTableAnchorEl(event.currentTarget);
            onFocusChange(true);
        };

        const handleTableClose = () => {
            setTableAnchorEl(null);
            setTablePickerSize(null);
            onFocusChange(false);
        };

        const handleTableSelect = (rows: number, columns: number) => {
            editor.chain().focus().insertTable({ rows, cols: columns, withHeaderRow: true }).run();
            handleTableClose();
        };

        const handleLinkClick = (event: React.MouseEvent<HTMLElement>) => {
            const previousUrl = editor.getAttributes("link").href;
            setLinkUrl(previousUrl || "");
            setLinkAnchorEl(event.currentTarget);
            onFocusChange(true);
        };

        const handleLinkClose = () => {
            setLinkAnchorEl(null);
            setLinkUrl("");
            onFocusChange(false);
        };

        const handleLinkApply = () => {
            if (linkUrl === "") {
                editor.chain().focus().extendMarkRange("link").unsetLink().run();
            } else {
                editor.chain().focus().extendMarkRange("link").setLink({ href: linkUrl }).run();
            }
            handleLinkClose();
        };

        const handleLinkRemove = () => {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
            handleLinkClose();
        };

        const handleEmojiClick = (event: React.MouseEvent<HTMLElement>) => {
            setEmojiAnchorEl(event.currentTarget);
            onFocusChange(true);
        };

        const handleEmojiClose = () => {
            setEmojiAnchorEl(null);
            onFocusChange(false);
        };

        const handleEmojiSelect = (emoji: string) => {
            editor.chain().focus().insertContent(emoji).run();
            handleEmojiClose();
        };

        const handleImageClick = (event: React.MouseEvent<HTMLElement>) => {
            const imageAttrs = editor.getAttributes("image");
            if (editor.isActive("image") && imageAttrs.src) {
                setImageUrl(imageAttrs.src || "");
                setImageWidth(imageAttrs.width ? String(imageAttrs.width) : "");
                setImageHeight(imageAttrs.height ? String(imageAttrs.height) : "");
                setImageAlign(imageAttrs.align || "left");
                setIsEditingImage(true);
            } else {
                setImageUrl("");
                setImageWidth("");
                setImageHeight("");
                setImageAlign("left");
                setIsEditingImage(false);
            }
            setImageAnchorEl(event.currentTarget);
            onFocusChange(true);
        };

        const handleImageClose = (keepFocus?: boolean) => {
            if (isEditingImage) {
                const { from } = editor.state.selection;
                editor
                    .chain()
                    .focus()
                    .setTextSelection(from + 1)
                    .run();
            }
            setImageAnchorEl(null);
            if (!keepFocus) {
                onFocusChange(false);
            }
        };

        const resetImageForm = () => {
            setImageUrl("");
            setImageWidth("");
            setImageHeight("");
            setImageAlign("left");
            setIsEditingImage(false);
        };

        const handleImageRemove = () => {
            editor.chain().focus().deleteSelection().run();
            handleImageClose();
        };

        const handleImageApply = () => {
            if (imageUrl) {
                const imageAttributes: Record<string, unknown> = { src: imageUrl, align: imageAlign };
                if (imageWidth) {
                    imageAttributes.width = parseInt(imageWidth, 10);
                } else {
                    imageAttributes.width = null;
                }
                if (imageHeight) {
                    imageAttributes.height = parseInt(imageHeight, 10);
                } else {
                    imageAttributes.height = null;
                }
                if (isEditingImage) {
                    editor.chain().focus().updateAttributes("image", imageAttributes).run();
                } else {
                    editor.chain().focus().setImage({ src: imageUrl }).run();
                    editor.chain().focus().updateAttributes("image", imageAttributes).run();
                }
            }
            handleImageClose();
        };

        const getHeadingText = () => {
            switch (toolbarState.headingLevel) {
                case 1:
                    return "H1";
                case 2:
                    return "H2";
                case 3:
                    return "H3";
                default:
                    return "Aa";
            }
        };

        return (
            <Box
                onMouseDown={(e) => {
                    e.preventDefault();
                }}
                sx={{
                    display: display ? "flex" : "none",
                    alignItems: "center",
                    gap: 0.5,
                    paddingTop: 1,
                    marginTop: 0.5,
                    borderTop: "1px solid",
                    borderColor: "divider",
                    flexWrap: "wrap",
                }}
            >
                <Tooltip title="Heading">
                    <IconButton
                        size="small"
                        onClick={handleHeadingClick}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        color={"primary"}
                    >
                        <Box component={"span"} sx={(theme) => ({ color: theme.palette.primary.main, fontWeight: "600", fontSize: 16 })}>
                            {getHeadingText()}
                        </Box>
                    </IconButton>
                </Tooltip>

                <Menu
                    anchorEl={headingAnchorEl}
                    open={headingOpen}
                    onClose={handleHeadingClose}
                    slotProps={{
                        paper: {
                            sx: { minWidth: 180 },
                            onMouseDown: (e: React.MouseEvent) => {
                                e.preventDefault();
                                e.stopPropagation();
                            },
                        },
                    }}
                >
                    <MenuItem
                        onClick={() => handleHeadingSelect(0)}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        sx={{ fontSize: "1rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                    >
                        Normal
                        {!toolbarState.isHeading && <CheckIcon fontSize="small" sx={{ ml: 2 }} />}
                    </MenuItem>
                    <MenuItem
                        onClick={() => handleHeadingSelect(1)}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        sx={{ fontSize: "1.5rem", fontWeight: "bold", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                    >
                        Heading 1{toolbarState.headingLevel === 1 && <CheckIcon fontSize="small" sx={{ ml: 2 }} />}
                    </MenuItem>
                    <MenuItem
                        onClick={() => handleHeadingSelect(2)}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        sx={{ fontSize: "1.25rem", fontWeight: "bold", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                    >
                        Heading 2{toolbarState.headingLevel === 2 && <CheckIcon fontSize="small" sx={{ ml: 2 }} />}
                    </MenuItem>
                    <MenuItem
                        onClick={() => handleHeadingSelect(3)}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        sx={{ fontSize: "1.1rem", fontWeight: "bold", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                    >
                        Heading 3{toolbarState.headingLevel === 3 && <CheckIcon fontSize="small" sx={{ ml: 2 }} />}
                    </MenuItem>
                    <MenuItem
                        onClick={() => handleHeadingSelect(4)}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        sx={{ fontSize: "1.0rem", fontWeight: "bold", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                    >
                        Heading 4{toolbarState.headingLevel === 4 && <CheckIcon fontSize="small" sx={{ ml: 2 }} />}
                    </MenuItem>
                </Menu>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <Tooltip title="Bold">
                    <IconButton
                        size="small"
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        color={toolbarState.isBold ? "primary" : "default"}
                        sx={{
                            backgroundColor: toolbarState.isBold ? "action.selected" : "transparent",
                        }}
                    >
                        <FormatBoldIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Italic">
                    <IconButton
                        size="small"
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        color={toolbarState.isItalic ? "primary" : "default"}
                        sx={{
                            backgroundColor: toolbarState.isItalic ? "action.selected" : "transparent",
                        }}
                    >
                        <FormatItalicIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Strikethrough">
                    <IconButton
                        size="small"
                        onClick={() => editor.chain().focus().toggleStrike().run()}
                        color={toolbarState.isStrike ? "primary" : "default"}
                        sx={{
                            backgroundColor: toolbarState.isStrike ? "action.selected" : "transparent",
                        }}
                    >
                        <StrikethroughSIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <Tooltip title="Link">
                    <IconButton
                        size="small"
                        onClick={handleLinkClick}
                        color={toolbarState.isLink ? "primary" : "default"}
                        sx={{
                            backgroundColor: toolbarState.isLink ? "action.selected" : "transparent",
                        }}
                    >
                        <LinkIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                <Menu
                    anchorEl={linkAnchorEl}
                    open={linkOpen}
                    onClose={handleLinkClose}
                    disableAutoFocus
                    disableEnforceFocus
                    disableRestoreFocus
                    slotProps={{
                        paper: {
                            sx: { minWidth: 320, p: 2 },
                        },
                    }}
                >
                    <Box
                        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                        onMouseDown={(e) => {
                            e.stopPropagation();
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                    >
                        <DotLegalTextField
                            autoFocus
                            label={"URL"}
                            placeholder="https://example.com"
                            value={linkUrl}
                            debounce={false}
                            onChange={(e) => setLinkUrl(e)}
                            noMargin
                        />
                        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", "& .MuiButtonBase-root": { height: "28px" } }}>
                            {toolbarState.isLink && (
                                <DotLegalButton
                                    size="small"
                                    buttonType="danger"
                                    startIcon={<LinkOffIcon sx={(theme) => ({ stroke: theme.customPalette.white, fill: theme.customPalette.white })} />}
                                    onClick={handleLinkRemove}
                                >
                                    {translateString("remove")}
                                </DotLegalButton>
                            )}
                            <Box sx={{ flex: 1 }} />
                            <DotLegalButton buttonType={"secondary"} size="small" onClick={handleLinkClose}>
                                {translateString("cancel")}
                            </DotLegalButton>
                            <DotLegalButton buttonType={"primary"} size="small" onClick={handleLinkApply}>
                                {translateString("save")}
                            </DotLegalButton>
                        </Box>
                    </Box>
                </Menu>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <Tooltip title="Bullet List">
                    <IconButton
                        size="small"
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                        color={toolbarState.isBulletList ? "primary" : "default"}
                        sx={{
                            backgroundColor: toolbarState.isBulletList ? "action.selected" : "transparent",
                        }}
                    >
                        <FormatListBulletedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Numbered List">
                    <IconButton
                        size="small"
                        onClick={() => editor.chain().focus().toggleOrderedList().run()}
                        color={toolbarState.isOrderedList ? "primary" : "default"}
                        sx={{
                            backgroundColor: toolbarState.isOrderedList ? "action.selected" : "transparent",
                        }}
                    >
                        <FormatListNumberedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <Tooltip title={translateString("insertTable")}>
                    <span>
                        <IconButton
                            size="small"
                            // Nested tables are not something the read-only renderers or the import path produce.
                            disabled={toolbarState.isTable}
                            onClick={handleTableClick}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                        >
                            <GridOnIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>

                <Menu
                    anchorEl={tableAnchorEl}
                    open={tableOpen}
                    onClose={handleTableClose}
                    disableAutoFocus
                    disableEnforceFocus
                    disableRestoreFocus
                    slotProps={{
                        paper: {
                            sx: { p: 1.5 },
                            onMouseDown: (e: React.MouseEvent) => {
                                e.preventDefault();
                                e.stopPropagation();
                            },
                        },
                    }}
                >
                    <Box
                        sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    >
                        <Box sx={{ textAlign: "center", fontSize: "0.875rem", fontWeight: 600 }}>
                            {tablePickerSize ? `${tablePickerSize.columns} x ${tablePickerSize.rows}` : translateString("insertTable")}
                        </Box>
                        <Box
                            sx={{ display: "grid", gridTemplateColumns: `repeat(${TABLE_PICKER_COLUMNS}, ${TABLE_PICKER_CELL_SIZE}px)`, gap: "3px" }}
                            onMouseLeave={() => setTablePickerSize(null)}
                        >
                            {Array.from({ length: TABLE_PICKER_ROWS * TABLE_PICKER_COLUMNS }).map((_, index) => {
                                const row = Math.floor(index / TABLE_PICKER_COLUMNS) + 1;
                                const column = (index % TABLE_PICKER_COLUMNS) + 1;
                                const selected = !!tablePickerSize && row <= tablePickerSize.rows && column <= tablePickerSize.columns;
                                return (
                                    <Box
                                        key={index}
                                        onMouseEnter={() => setTablePickerSize({ rows: row, columns: column })}
                                        onClick={() => handleTableSelect(row, column)}
                                        sx={{
                                            width: TABLE_PICKER_CELL_SIZE,
                                            height: TABLE_PICKER_CELL_SIZE,
                                            border: "1px solid",
                                            borderColor: selected ? "primary.main" : "divider",
                                            backgroundColor: selected ? "action.selected" : "transparent",
                                            cursor: "pointer",
                                        }}
                                    />
                                );
                            })}
                        </Box>
                    </Box>
                </Menu>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <Tooltip title="Emoji">
                    <IconButton
                        size="small"
                        onClick={handleEmojiClick}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    >
                        <EmojiEmotionsIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                <Menu
                    anchorEl={emojiAnchorEl}
                    open={emojiOpen}
                    onClose={handleEmojiClose}
                    slotProps={{
                        paper: {
                            sx: { maxHeight: 300, width: 320 },
                            onMouseDown: (e: React.MouseEvent) => {
                                e.preventDefault();
                                e.stopPropagation();
                            },
                        },
                    }}
                >
                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: 0.5, p: 1 }}>
                        {commonEmojis.map((emoji) => (
                            <Box
                                key={emoji}
                                onClick={() => handleEmojiSelect(emoji)}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                                sx={{
                                    fontSize: "24px",
                                    lineHeight: 1,
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 32,
                                    height: 32,
                                    borderRadius: 1,
                                    transition: "background-color 0.2s",
                                    "&:hover": {
                                        backgroundColor: "action.hover",
                                    },
                                }}
                            >
                                {emoji}
                            </Box>
                        ))}
                    </Box>
                </Menu>

                {enableImageUpload && (
                    <>
                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                        <Tooltip title={translateString("image")}>
                            <IconButton
                                size="small"
                                onClick={handleImageClick}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                                color={toolbarState.isImage ? "primary" : "default"}
                                sx={{
                                    backgroundColor: toolbarState.isImage ? "action.selected" : "transparent",
                                }}
                            >
                                <ImageIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        <Menu
                            anchorEl={imageAnchorEl}
                            open={imageOpen}
                            onClose={(_, reason) => {
                                if (reason === "backdropClick") {
                                    handleImageClose(true);
                                    editor.commands.focus();
                                } else {
                                    handleImageClose();
                                }
                            }}
                            disableAutoFocus
                            disableEnforceFocus
                            disableRestoreFocus
                            TransitionProps={{
                                onExited: resetImageForm,
                            }}
                            slotProps={{
                                paper: {
                                    sx: { minWidth: 320, p: 2 },
                                },
                            }}
                        >
                            <Box
                                ref={imageMenuRef}
                                sx={{ display: "flex", flexDirection: "column", gap: 2, position: "relative" }}
                                onMouseDown={(e) => {
                                    e.stopPropagation();
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                }}
                                onKeyDown={handleImageKeyDown}
                            >
                                <IconButton size="small" onClick={() => handleImageClose()} sx={{ position: "absolute", top: -8, right: -8 }}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                                <Box sx={{ height: 16 }} />
                                {!isEditingImage && (
                                    <DotLegalTextField
                                        autoFocus
                                        label={"URL"}
                                        placeholder="https://example.com/image.png"
                                        value={imageUrl}
                                        debounce={false}
                                        onChange={(e) => onImageUrlChange(e)}
                                        noMargin
                                    />
                                )}
                                <Box sx={{ display: "flex", gap: 1 }}>
                                    <DotLegalTextField
                                        label={translateString("width")}
                                        placeholder="px"
                                        value={imageWidth}
                                        debounce={false}
                                        onChange={(e) => setImageWidth(e)}
                                        noMargin
                                    />
                                    <DotLegalTextField
                                        label={translateString("height")}
                                        placeholder="px"
                                        value={imageHeight}
                                        debounce={false}
                                        onChange={(e) => setImageHeight(e)}
                                        noMargin
                                    />
                                </Box>
                                <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                                    <IconButton
                                        size="small"
                                        onClick={() => setImageAlign("left")}
                                        color={imageAlign === "left" ? "primary" : "default"}
                                        sx={{ backgroundColor: imageAlign === "left" ? "action.selected" : "transparent" }}
                                    >
                                        <FormatAlignLeftIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() => setImageAlign("center")}
                                        color={imageAlign === "center" ? "primary" : "default"}
                                        sx={{ backgroundColor: imageAlign === "center" ? "action.selected" : "transparent" }}
                                    >
                                        <FormatAlignCenterIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() => setImageAlign("right")}
                                        color={imageAlign === "right" ? "primary" : "default"}
                                        sx={{ backgroundColor: imageAlign === "right" ? "action.selected" : "transparent" }}
                                    >
                                        <FormatAlignRightIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                                <Box sx={{ display: "flex", gap: 1, justifyContent: "center", "& .MuiButtonBase-root": { height: "28px" } }}>
                                    {isEditingImage && (
                                        <DotLegalButton size="small" buttonType="danger" onClick={handleImageRemove}>
                                            {translateString("remove")}
                                        </DotLegalButton>
                                    )}
                                    <DotLegalButton buttonType={"primary"} size="small" onClick={handleImageApply}>
                                        {translateString(isEditingImage ? "save" : "insert")}
                                    </DotLegalButton>
                                </Box>
                            </Box>
                        </Menu>
                    </>
                )}

                {!disableMentions && (
                    <Tooltip title="Mention">
                        <IconButton size="small" onClick={onMentionClick}>
                            <AlternateEmailIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        );
    }
);

MentionInputToolbar.displayName = "MentionInputToolbar";

export default MentionInputToolbar;
